import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFriendRequest,
  decideApproval,
  findFriends,
  getActivity,
  getApprovals,
  listFriends,
  pingCircle,
  sendModeratorMessage,
  sendParentNote,
} from '../lib/api/friends'
import type {
  CreateFriendRequest,
  ModeratorMessageRequest,
  ParentNoteRequest,
} from '../types/community'

// COTC_FriendsApprovals — react-query hooks over the friends api module.

export function useApprovals(enabled = true) {
  return useQuery({ queryKey: ['community', 'approvals'], queryFn: getApprovals, enabled })
}

export function useActivity(enabled = true) {
  return useQuery({ queryKey: ['community', 'activity'], queryFn: getActivity, enabled })
}

export function useFriends(actingPersonId?: string, circleId?: string, enabled = true) {
  return useQuery({
    queryKey: ['community', 'friends', actingPersonId ?? '', circleId ?? ''],
    queryFn: () => listFriends(circleId),
    enabled,
  })
}

export function useDirectory(actingPersonId?: string, filter?: string, enabled = true) {
  return useQuery({
    queryKey: ['community', 'directory', actingPersonId ?? '', filter ?? ''],
    queryFn: () => findFriends(filter),
    enabled,
  })
}

export function useDecideApproval() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { kind: string; id: string; decision: 'approve' | 'decline' }) =>
      decideApproval(v.kind, v.id, v.decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community', 'approvals'] })
      qc.invalidateQueries({ queryKey: ['community', 'activity'] })
      // Approval outcomes can change membership, directory eligibility and pending requests.
      for (const domain of ['groups', 'group', 'group-join-requests', 'group-invitations', 'browse', 'circles', 'circle', 'friends', 'directory', 'roster']) {
        void qc.invalidateQueries({ queryKey: ['community', domain] })
      }
      qc.invalidateQueries({ queryKey: ['community', 'me'] })
    },
  })
}

export function useCreateFriendRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateFriendRequest) => createFriendRequest(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community', 'directory'] })
      qc.invalidateQueries({ queryKey: ['community', 'approvals'] })
      qc.invalidateQueries({ queryKey: ['community', 'me'] })
    },
  })
}

export function usePingCircle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => pingCircle(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['community', 'activity'] }),
  })
}

export function useSendParentNote() {
  return useMutation({ mutationFn: (req: ParentNoteRequest) => sendParentNote(req) })
}

export function useSendModeratorMessage() {
  return useMutation({ mutationFn: (req: ModeratorMessageRequest) => sendModeratorMessage(req) })
}
